import React, { useReducer, useContext, useEffect, useRef, useState } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import { modifiersReducer } from './modifiersReducer';
import {
	IModifiersByCustomerV2,
	IGetPaginatedModifiersByCustomerV2,
	IModifiersByRestaurantV2,
	IGetPaginatedModifiersByRestaurantV2,
	SortByForPaginatedModifiers,
	DirectionForPaginated,
	IItemForGetShortInfo,
} from 'types';
import { PRE_SELECTED_CUSTOMER_ID_FOR_MENU } from 'appConstants';
import { ModifiersActionTypes } from './modifiersActions';
import {
	modifiersInitialState,
	ModifiersContext,
	TSortBy,
	TCountOfItems,
	IFilterValues,
	TTypeOfModalWindow,
} from './modifiersContext';
import {
	getPaginatedModifiersByCustomerV2,
	getPaginatedModifiersByRestaurantV2,
	getModifierPhotos,
	deleteModifierV2,
} from 'backend/modifiers';
import { createAppError } from 'utils/helpers';
import { AxiosResponse } from 'axios';
import { useFetchData } from 'hooks/useFetchData';

export const ModifiersState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(modifiersReducer, modifiersInitialState);
	const { showLoader, hideLoader, addAppError } = useContext<IAppContext>(AppContext);
	const [allowChangingSearchString, setAllowChangingSearchString] = useState(false);
	const isMounted = useRef<boolean>(false);
	const {
		refetch: refetchModifiersByCustomer,
		response: responseModifiersByCustomer,
		loading: loadingModifiersByCustomer,
	} = useFetchData<AxiosResponse<IModifiersByCustomerV2>, IGetPaginatedModifiersByCustomerV2>(
		isMounted.current && state.IDSelectedCustomer && !state.IDSelectedRestaurant
			? getPaginatedModifiersByCustomerV2
			: null,
		{
			pageNumber: state.currentPage,
			pageSize: Number(state.countOfItems),
			searchInput: state.searchString ? state.searchString : null,
			customerId: state.IDSelectedCustomer || '',
			sorting: state.sortBy
				? {
						sortBy: {
							'ModifiersA-Z': SortByForPaginatedModifiers.Name,
							'ModifiersZ-A': SortByForPaginatedModifiers.Name,
						}[state.sortBy],
						direction: {
							'ModifiersA-Z': DirectionForPaginated.Ascending,
							'ModifiersZ-A': DirectionForPaginated.Descending,
						}[state.sortBy],
				  }
				: null,
		},
	);
	const {
		refetch: refetchModifiersByRestaurant,
		response: responseModifiersByRestaurant,
		loading: loadingModifiersByRestaurant,
	} = useFetchData<AxiosResponse<IModifiersByRestaurantV2>, IGetPaginatedModifiersByRestaurantV2>(
		isMounted.current && state.IDSelectedRestaurant ? getPaginatedModifiersByRestaurantV2 : null,
		{
			pageNumber: state.currentPage,
			pageSize: Number(state.countOfItems),
			searchInput: state.searchString ? state.searchString : null,
			restaurantId: state.IDSelectedRestaurant || '',
			modifierGroupId: state.IDSelectedModifierGroup,
			isDisabled: state.isDisabled,
			isRestricted: state.isRestricted,
			sorting: state.sortBy
				? {
						sortBy: {
							'ModifiersA-Z': SortByForPaginatedModifiers.Name,
							'ModifiersZ-A': SortByForPaginatedModifiers.Name,
						}[state.sortBy],
						direction: {
							'ModifiersA-Z': DirectionForPaginated.Ascending,
							'ModifiersZ-A': DirectionForPaginated.Descending,
						}[state.sortBy],
				  }
				: null,
		},
	);

	useEffect(() => {
		loadingModifiersByCustomer || loadingModifiersByRestaurant ? showLoader() : hideLoader();
	}, [loadingModifiersByCustomer, loadingModifiersByRestaurant]);

	useEffect(() => {
		if (responseModifiersByCustomer) {
			dispatch({
				type: ModifiersActionTypes.SET_MODIFIERS_BY_CUSTOMER,
				payload: {
					modifiersByCustomer: responseModifiersByCustomer.data,
					typeOfModalWindow: state.typeOfModalWindow,
				},
			});
			setAllowChangingSearchString(true);

			const getModifiersPreSignedUrl = async (modifierIds: string[]) => {
				try {
					const modifiersPhotosResponse = await getModifierPhotos({ ids: modifierIds, size: 1 });
					const updatedModifiersByCustomer = responseModifiersByCustomer.data.items.reduce(
						(acc: IModifiersByCustomerV2['items'], item) => {
							const modifiersPhotoFile = modifiersPhotosResponse.data.files.find(
								(photoFile) => photoFile.id === item.id,
							);
							acc.push({
								...item,
								preSignedUrl: modifiersPhotoFile ? modifiersPhotoFile.preSignedUrl : null,
							});
							return acc;
						},
						[],
					);

					dispatch({
						type: ModifiersActionTypes.SET_MODIFIERS_BY_CUSTOMER,
						payload: {
							modifiersByCustomer: {
								totalCount: responseModifiersByCustomer.data.totalCount,
								items: updatedModifiersByCustomer,
							},
							typeOfModalWindow: state.typeOfModalWindow,
						},
					});
				} catch (err) {
					const error = err as Error;
					const appError = createAppError(error);
					addAppError(appError);
				}
			};

			const modsIds = responseModifiersByCustomer.data.items.map((mod) => mod.id);
			getModifiersPreSignedUrl(modsIds);
		}
	}, [responseModifiersByCustomer]);

	useEffect(() => {
		if (responseModifiersByRestaurant) {
			dispatch({
				type: ModifiersActionTypes.SET_MODIFIERS_BY_RESTAURANT,
				payload: {
					modifiersByRestaurant: responseModifiersByRestaurant.data,
					typeOfModalWindow: state.typeOfModalWindow,
				},
			});
			setAllowChangingSearchString(true);

			const getModifiersPreSignedUrl = async (modifierIds: string[]) => {
				try {
					const modifiersPhotosResponse = await getModifierPhotos({ ids: modifierIds, size: 1 });
					const updatedModifiersByRestaurant = responseModifiersByRestaurant.data.items.reduce(
						(acc: IModifiersByRestaurantV2['items'], item) => {
							const ModifiersPhotoFile = modifiersPhotosResponse.data.files.find(
								(photoFile) => photoFile.id === item.id,
							);
							acc.push({
								...item,
								preSignedUrl: ModifiersPhotoFile ? ModifiersPhotoFile.preSignedUrl : null,
							});
							return acc;
						},
						[],
					);

					dispatch({
						type: ModifiersActionTypes.SET_MODIFIERS_BY_RESTAURANT,
						payload: {
							modifiersByRestaurant: {
								totalCount: responseModifiersByRestaurant.data.totalCount,
								items: updatedModifiersByRestaurant,
							},
							typeOfModalWindow: state.typeOfModalWindow,
						},
					});
				} catch (err) {
					const error = err as Error;
					const appError = createAppError(error);
					addAppError(appError);
				}
			};

			const modsIds = responseModifiersByRestaurant.data.items.map((mod) => mod.id);
			getModifiersPreSignedUrl(modsIds);
		}
	}, [responseModifiersByRestaurant]);

	useEffect(() => {
		if (state.needRefetchData) {
			refetchModifiersByCustomer();
			refetchModifiersByRestaurant();
			dispatch({
				type: ModifiersActionTypes.REFETCH_DATA,
				payload: {
					needRefetchData: false,
				},
			});
		}
	}, [state.needRefetchData]);

	const refetchData = (value: boolean) => {
		dispatch({
			type: ModifiersActionTypes.REFETCH_DATA,
			payload: {
				needRefetchData: value,
			},
		});
	};

	const deleteSelectedItem = async () => {
		try {
			showLoader();
			await deleteModifierV2(state.IDForDeletion);

			if (state.modifiersByCustomer.totalCount) {
				const filteredModifiersByCustomer = state.modifiersByCustomer.items.filter(
					(el) => el.id !== state.IDForDeletion,
				);
				dispatch({
					type: ModifiersActionTypes.SET_MODIFIERS_BY_CUSTOMER,
					payload: {
						modifiersByCustomer: {
							totalCount: state.modifiersByCustomer.totalCount - 1,
							items: filteredModifiersByCustomer,
						},
						typeOfModalWindow: 'success',
					},
				});
			}

			if (state.modifiersByRestaurant.totalCount) {
				const filteredModifiersByRestaurant = state.modifiersByRestaurant.items.filter(
					(el) => el.id !== state.IDForDeletion,
				);
				dispatch({
					type: ModifiersActionTypes.SET_MODIFIERS_BY_RESTAURANT,
					payload: {
						modifiersByRestaurant: {
							totalCount: state.modifiersByRestaurant.totalCount - 1,
							items: filteredModifiersByRestaurant,
						},
						typeOfModalWindow: 'success',
					},
				});
			}
		} catch (err) {
			dispatch({
				type: ModifiersActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
				payload: {
					typeOfModalWindow: 'error',
				},
			});
		} finally {
			hideLoader();
		}
	};

	const changeSearchString = (value: string) => {
		if (allowChangingSearchString) {
			dispatch({
				type: ModifiersActionTypes.CHANGE_SEARCH_STRING,
				payload: {
					searchString: value,
				},
			});
		}
	};

	const toggleRememberMyChoice = () => {
		!state.rememberMyChoice && state.IDSelectedCustomer
			? localStorage.setItem(PRE_SELECTED_CUSTOMER_ID_FOR_MENU, state.IDSelectedCustomer)
			: localStorage.removeItem(PRE_SELECTED_CUSTOMER_ID_FOR_MENU);

		dispatch({
			type: ModifiersActionTypes.TOGGLE_REMEMBER_MY_CHOICE,
			payload: {
				rememberMyChoice: !state.rememberMyChoice,
			},
		});
	};

	const setFetchedCustomers = (value: IItemForGetShortInfo[]) => {
		dispatch({
			type: ModifiersActionTypes.SET_FETCHED_CUSTOMERS,
			payload: {
				fetchedCustomers: value,
			},
		});
	};

	const setIDCustomer = (value: Nullable<string>) => {
		value && state.rememberMyChoice
			? localStorage.setItem(PRE_SELECTED_CUSTOMER_ID_FOR_MENU, value)
			: localStorage.removeItem(PRE_SELECTED_CUSTOMER_ID_FOR_MENU);

		dispatch({
			type: ModifiersActionTypes.SET_ID_CUSTOMER,
			payload: {
				IDSelectedCustomer: value ? value : null,
				modifiersByCustomer: value ? state.modifiersByCustomer : { totalCount: 0, items: [] },
				modifiersByRestaurant: value ? state.modifiersByRestaurant : { totalCount: 0, items: [] },
			},
		});
	};

	const setIDRestaurant = (value: Nullable<string>) => {
		dispatch({
			type: ModifiersActionTypes.SET_ID_RESTAURANT,
			payload: {
				IDSelectedRestaurant: value ? value : null,
				isDisabled: value ? state.isDisabled : null,
				isRestricted: value ? state.isRestricted : false,
			},
		});
	};

	const setIDModifierGroup = (value: Nullable<string>) => {
		dispatch({
			type: ModifiersActionTypes.SET_ID_MODIFIER_GROUP,
			payload: {
				IDSelectedModifierGroup: value,
			},
		});
	};

	const toggleIsDisabled = () => {
		let newIsDisabled;
		if (state.isDisabled === null) {
			newIsDisabled = false;
		} else if (state.isDisabled === false) {
			newIsDisabled = true;
		} else {
			newIsDisabled = null;
		}

		dispatch({
			type: ModifiersActionTypes.TOGGLE_IS_DISABLED,
			payload: {
				isDisabled: newIsDisabled,
			},
		});
	};

	const toggleIsRestricted = () => {
		dispatch({ type: ModifiersActionTypes.TOGGLE_IS_RESTRICTED });
	};

	const resetSelectedControls = () => dispatch({ type: ModifiersActionTypes.RESET_SELECTED_CONTROLS });

	const setSortBy = (sortBy: TSortBy) => {
		dispatch({
			type: ModifiersActionTypes.SET_SORT_BY,
			payload: {
				sortBy: sortBy,
			},
		});
	};

	const setCountOfItems = (count: TCountOfItems) => {
		dispatch({
			type: ModifiersActionTypes.SET_COUNT_OF_ITEMS,
			payload: {
				countOfItems: count,
			},
		});
	};

	const changeCurrentPage = (page: number) => {
		dispatch({
			type: ModifiersActionTypes.CHANGE_CURRENT_PAGE,
			payload: {
				currentPage: page,
			},
		});
	};

	const setIDForDeletion = (value: string) => {
		dispatch({
			type: ModifiersActionTypes.SET_ID_FOR_DELETION,
			payload: {
				IDForDeletion: value,
				typeOfModalWindow: 'toConfirmDeleteItem',
			},
		});
	};

	const setIDForAssignRestaurants = (value: string) => {
		dispatch({
			type: ModifiersActionTypes.SET_ID_FOR_ASSIGN_RESTAURANTS,
			payload: {
				IDForAssignRestaurants: value,
				typeOfModalWindow: value ? 'assignRestaurants' : null,
			},
		});
	};

	const setIDForEditModGroupTemplate = (value: string) => {
		dispatch({
			type: ModifiersActionTypes.SET_ID_FOR_EDIT_MOD_GROUP_TEMPLATE,
			payload: {
				IDForEditModGroupTemplate: value,
				typeOfModalWindow: value ? 'editNestedModGroupTemplate' : null,
			},
		});
	};

	const changeTypeOfModalWindow = (value: TTypeOfModalWindow) => {
		dispatch({
			type: ModifiersActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	const setFiltersFromQueryString = (value: IFilterValues) => {
		!isMounted.current &&
			value.IDSelectedCustomer &&
			localStorage.setItem(PRE_SELECTED_CUSTOMER_ID_FOR_MENU, value.IDSelectedCustomer);

		dispatch({
			type: ModifiersActionTypes.SET_FILTERS_FROM_QUERY_STRING,
			payload: {
				filters: {
					...value,
					IDSelectedCustomer:
						value.IDSelectedCustomer || localStorage.getItem(PRE_SELECTED_CUSTOMER_ID_FOR_MENU),
				},
			},
		});
		isMounted.current = true;
		refetchModifiersByCustomer();
		refetchModifiersByRestaurant();
	};

	return (
		<ModifiersContext.Provider
			value={{
				refetchData,
				deleteSelectedItem,
				changeSearchString,
				toggleRememberMyChoice,
				setFetchedCustomers,
				setIDCustomer,
				setIDRestaurant,
				setIDModifierGroup,
				toggleIsDisabled,
				toggleIsRestricted,
				resetSelectedControls,
				setSortBy,
				setCountOfItems,
				changeCurrentPage,
				setIDForDeletion,
				setIDForAssignRestaurants,
				setIDForEditModGroupTemplate,
				changeTypeOfModalWindow,
				setFiltersFromQueryString,
				needRefetchData: state.needRefetchData,
				modifiersByCustomer: state.modifiersByCustomer,
				modifiersByRestaurant: state.modifiersByRestaurant,
				searchString: state.searchString,
				rememberMyChoice: state.rememberMyChoice,
				fetchedCustomers: state.fetchedCustomers,
				IDSelectedCustomer: state.IDSelectedCustomer,
				IDSelectedRestaurant: state.IDSelectedRestaurant,
				IDSelectedModifierGroup: state.IDSelectedModifierGroup,
				isDisabled: state.isDisabled,
				isRestricted: state.isRestricted,
				sortBy: state.sortBy,
				countOfItems: state.countOfItems,
				currentPage: state.currentPage,
				IDForDeletion: state.IDForDeletion,
				IDForAssignRestaurants: state.IDForAssignRestaurants,
				IDForEditModGroupTemplate: state.IDForEditModGroupTemplate,
				isInitializedSelectCustomerModal: state.isInitializedSelectCustomerModal,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</ModifiersContext.Provider>
	);
};
